// The KYC review workspace — a full page, because reviewing an application is
// the job, not a side-glance.
//
// This work used to happen in a drawer beside the queue: a ~400px column
// holding company data, people, twelve documents, an identity table, the edit
// facility and the decision. Documents could not be looked at properly, the
// people and their checks were a two-line table, and the decision sat below a
// scroll of everything else. A reviewer cannot be asked to approve a company
// through a letterbox.
//
// The page is organised the way the review actually proceeds: who they are →
// the people and their identity checks → the documents → the decision, with
// what blocks the decision stated at the top where it cannot be missed.

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import {
  onboardingApi,
  type ApplicationDecision,
  type DocumentReview,
} from '../../../api/onboarding.api.js';
import { useAuth } from '../../../auth/useAuth.js';
import { BreadcrumbLabel } from '../../../layout/breadcrumb-labels.js';
import { CommentsPanel } from '../../../components/CommentsPanel.js';
import { CompanyScreeningPanel } from '../../../components/CompanyScreeningPanel.js';
import { PartyVerificationPanel } from '../../../components/PartyVerificationPanel.js';
import { StatusBadge } from '../../../components/StatusBadge.js';

import { customersApi } from '../../../api/customers.api.js';
import { SetRiskRatingButton } from '../../customers/SetRiskRatingButton.js';
import { ApprovalWarnings } from '../../kyc/ApprovalBlockers.js';
import { screeningReviewBanner, splitScreeningWarnings } from '../../kyc/kyc-approval.js';

import { DocumentReviewSection } from './DocumentReviewSection.js';
import { PeopleSection } from './PeopleSection.js';
import { SnapshotView } from './SnapshotView.js';
import { EditContext, ReviewContext, asRecord, asString, type PendingEdit } from './shared.js';
import { DeclaredInformationPanel } from '../../../components/DeclaredInformationPanel.js';
import { APP_LOCALE } from '../../../utils/locale.js';

const DECISION_LABEL: Record<ApplicationDecision, string> = {
  APPROVE: 'Approve',
  DECLINE: 'Decline',
  REQUEST_INFO: 'Request info',
};

export function ApplicationReviewPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canDecide = user?.role === 'compliance' || user?.role === 'admin';

  const [tab, setTab] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [decision, setDecision] = useState<ApplicationDecision | null>(null);
  const [reason, setReason] = useState('');

  const { data: detail, isLoading, error } = useQuery({
    queryKey: ['onboarding-application', id],
    queryFn: async () => onboardingApi.getApplication(id),
    enabled: id !== '',
  });

  const { data: reviewState, refetch: refetchReview } = useQuery({
    queryKey: ['application-review-state', id],
    queryFn: async () => onboardingApi.reviewState(id),
    enabled: id !== '',
    retry: false,
  });

  const { data: decisionHistory } = useQuery({
    queryKey: ['application-decision-history', id],
    queryFn: async () => onboardingApi.applicationHistory(id),
    enabled: id !== '',
  });
  const { data: editHistory } = useQuery({
    queryKey: ['application-edits', id],
    queryFn: async () => onboardingApi.applicationEdits(id),
    enabled: id !== '',
  });

  // The parties are needed for the tab counter and to pair each declared person
  // with the identity check that actually ran for them.
  const customerId = detail?.customerId ?? '';
  const { data: partiesData } = useQuery({
    queryKey: ['verification-parties', customerId],
    queryFn: async () => customersApi.verificationParties(customerId),
    enabled: customerId !== '' && detail?.accountType === 'BUSINESS',
    retry: false,
  });

  // Agent edits: staged locally, saved as one batch with a stated reason.
  const [editing, setEditing] = useState(false);
  const [pendingEdits, setPendingEdits] = useState<Map<string, PendingEdit>>(new Map());
  const [editReason, setEditReason] = useState('');
  const [editError, setEditError] = useState('');
  const stageEdit = (path: string, label: string, oldValue: string, value: string): void => {
    setPendingEdits((prev) => {
      const next = new Map(prev);
      if (value === oldValue) next.delete(path);
      else next.set(path, { label, oldValue, value });
      return next;
    });
  };
  const resetEditing = (): void => {
    setEditing(false);
    setPendingEdits(new Map());
    setEditReason('');
    setEditError('');
  };
  // Staged edits belong to ONE application; navigating to another must not
  // carry them across.
  useEffect(() => {
    resetEditing();
  }, [id]);

  const editMutation = useMutation({
    mutationFn: async () =>
      onboardingApi.editApplication(
        id,
        [...pendingEdits.entries()].map(([path, edit]) => ({ path, value: edit.value })),
        editReason.trim(),
      ),
    onSuccess: async () => {
      resetEditing();
      await queryClient.invalidateQueries({ queryKey: ['onboarding-application', id] });
      await queryClient.invalidateQueries({ queryKey: ['application-edits', id] });
    },
    onError: (err: unknown) => {
      setEditError(err instanceof Error ? err.message : 'The edit could not be saved.');
    },
  });

  const reviewsById = useMemo(() => {
    const map = new Map<string, DocumentReview>();
    for (const review of reviewState?.documents ?? []) map.set(review.id, review);
    return map;
  }, [reviewState]);

  const decideDocument = async (
    docId: string,
    docDecision: 'VERIFY' | 'REJECT',
    note?: string,
  ): Promise<void> => {
    await onboardingApi.decideDocument(id, docId, docDecision, note);
    await refetchReview();
  };

  const removeDocument = async (docId: string, kind: string, reason: string): Promise<void> => {
    await onboardingApi.removeDocument(id, docId, kind, reason);
    await refetchReview();
  };

  const renameDocument = async (docId: string, name: string): Promise<void> => {
    await onboardingApi.renameDocument(id, docId, name);
    await refetchReview();
  };

  const recordDocumentDates = async (
    docId: string,
    dates: { issuedAt: string | null; expiresAt: string | null },
  ): Promise<void> => {
    await onboardingApi.recordDocumentDates(id, docId, dates);
    await refetchReview();
  };

  const decisionMutation = useMutation({
    mutationFn: async (input: { decision: ApplicationDecision; reason: string }) =>
      onboardingApi.decide(id, input.decision, input.reason),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['onboarding-applications'] });
      await queryClient.invalidateQueries({ queryKey: ['onboarding-application', id] });
      await queryClient.invalidateQueries({ queryKey: ['application-decision-history', id] });
      await refetchReview();
      setConfirmOpen(false);
      setReason('');
    },
  });

  if (id === '') return <Alert severity="error">Missing application id</Alert>;
  if (isLoading) {
    return (
      <Stack alignItems="center" py={6}>
        <CircularProgress />
      </Stack>
    );
  }
  if (error !== null && error !== undefined) {
    return <Alert severity="error">Failed to load the application: {error.message}</Alert>;
  }
  if (detail === undefined || detail === null) {
    return <Alert severity="warning">Application not found.</Alert>;
  }

  const snapshot = asRecord(detail.snapshot);
  const business = asRecord(snapshot['business']);
  const company = asRecord(business['company']);
  const title =
    asString(company['legalName']) ||
    asString(asRecord(snapshot['details'])['fullName']) ||
    detail.email;
  const isBusiness = detail.accountType === 'BUSINESS';
  const decidable =
    detail.status === 'SUBMITTED' ||
    detail.status === 'IN_PROGRESS' ||
    detail.status === 'INFO_REQUESTED';
  const readiness = reviewState?.readiness;
  const blocked = readiness !== undefined && !readiness.ready;
  // Screening warns, it does not block (DEV-114).
  const screeningBanner = screeningReviewBanner(readiness);
  const screeningHasMatch = splitScreeningWarnings(readiness).matches.length > 0;

  const openConfirm = (d: ApplicationDecision): void => {
    setDecision(d);
    setConfirmOpen(true);
  };

  return (
    <>
      <BreadcrumbLabel id={id} label={title} />
      {/* Who and what, then the decision — the two things a reviewer needs in
          view at all times, kept out of the scrolling body. */}
      <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
        <Button component={RouterLink} to="/onboarding-applications" size="small">
          ← Queue
        </Button>
        <Typography variant="h5" sx={{ flexGrow: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
          {title}
        </Typography>
        <Chip size="small" label={detail.accountType} />
        <StatusBadge kind="onboarding" value={detail.status} />
        {detail.kycStatus !== '' ? (
          <StatusBadge kind="kycCase" value={detail.kycStatus} />
        ) : null}
        {detail.riskRating !== '' ? <StatusBadge kind="risk" value={detail.riskRating} /> : null}
        {canDecide ? (
          <SetRiskRatingButton key={detail.customerId} customerId={detail.customerId} current={detail.riskRating} />
        ) : null}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        {detail.email} · {detail.country || '—'} · started{' '}
        {new Date(detail.createdAt).toLocaleDateString(APP_LOCALE)} · last activity{' '}
        {new Date(detail.updatedAt).toLocaleString(APP_LOCALE)} ·{' '}
        <RouterLink to={`/customers/${detail.customerId}`}>customer record</RouterLink>
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ md: 'center' }}
          justifyContent="space-between"
        >
          <Box sx={{ flexGrow: 1 }}>
            {readiness === undefined ? (
              <Typography variant="body2" color="text.secondary">
                Checking what is outstanding…
              </Typography>
            ) : readiness.ready && screeningBanner !== null ? (
              // "All checks complete" would be untrue with no screening result,
              // and a possible match is louder than a missing one.
              <Alert severity={screeningHasMatch ? 'error' : 'warning'} sx={{ py: 0 }}>
                {screeningBanner}
              </Alert>
            ) : readiness.ready ? (
              <Alert severity="success" sx={{ py: 0 }}>
                All checks complete — this application can be approved.
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ py: 0 }}>
                Approval is blocked:{' '}
                {readiness.blockers.map((blocker) => blocker.detail).join(' ')}
              </Alert>
            )}
            {readiness !== undefined ? (
              <Typography variant="caption" color="text.secondary">
                Documents: {String(readiness.documents.verified)} approved,{' '}
                {String(readiness.documents.rejected)} rejected,{' '}
                {String(readiness.documents.pending)} awaiting review · identity checks passed{' '}
                {String(readiness.parties.approved)}/{String(readiness.parties.total)}
                {readiness.documents.rejected > 0
                  ? ' · a rejected document does not block approval — it is a decision the reviewer already took'
                  : ''}
              </Typography>
            ) : null}
          </Box>
          {canDecide && decidable ? (
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                color="success"
                disabled={decisionMutation.isPending || blocked}
                title={blocked ? readiness?.blockers.map((b) => b.detail).join(' ') : undefined}
                onClick={() => openConfirm('APPROVE')}
              >
                Approve application
              </Button>
              <Button
                variant="outlined"
                color="warning"
                disabled={decisionMutation.isPending}
                onClick={() => openConfirm('REQUEST_INFO')}
              >
                Request info
              </Button>
              <Button
                variant="contained"
                color="error"
                disabled={decisionMutation.isPending}
                onClick={() => openConfirm('DECLINE')}
              >
                Decline application
              </Button>
            </Stack>
          ) : null}
        </Stack>
        {decisionMutation.isError ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            {decisionMutation.error instanceof Error
              ? decisionMutation.error.message
              : 'The decision was not recorded.'}
          </Alert>
        ) : null}
      </Paper>

      <Paper>
        <Tabs value={tab} onChange={(_, next: number) => setTab(next)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ px: 2 }}>
          <Tab label="Applicant" />
          <Tab
            label={
              isBusiness
                ? `People & identity${
                    partiesData === undefined
                      ? ''
                      : ` (${String(partiesData.parties.length)})`
                  }`
                : 'Identity'
            }
          />
          <Tab
            label={`Documents${
              reviewState === undefined ? '' : ` (${String(reviewState.documents.length)})`
            }`}
          />
          <Tab label={`History (${String((editHistory ?? []).length + (decisionHistory ?? []).length)})`} />
        </Tabs>
        <Divider />

        <Box sx={{ p: 2 }}>
          {tab === 0 ? (
            <>
              {/* Edits stay opt-in: a reviewer reads far more often than they
                  correct, and an always-editable form invites accidents. */}
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Button
                  size="small"
                  variant={editing ? 'contained' : 'outlined'}
                  onClick={() => (editing ? resetEditing() : setEditing(true))}
                >
                  {editing
                    ? 'Cancel editing'
                    : decidable
                      ? 'Edit application'
                      : 'Complete missing details'}
                </Button>
                {!decidable ? (
                  /* A decided application is evidence, so nothing it was
                     decided on may be rewritten — but a field nobody was ever
                     asked for can still be added. Onboarding did not collect a
                     personal applicant's citizenship until recently, so every
                     approved personal application is missing one, and without
                     this there was no way to supply it. */
                  <Typography variant="caption" color="text.secondary">
                    Decided: existing answers are evidence and stay as they are. Fields left
                    empty can still be completed.
                  </Typography>
                ) : null}
              </Stack>

              {/* A live business client's declaration stays current through its
                  own audited path; the decided answers above stay evidence. */}
              {isBusiness && detail.status === 'APPROVED' ? (
                <Box sx={{ mb: 2 }}>
                  <DeclaredInformationPanel customerId={detail.customerId} />
                </Box>
              ) : null}

              {/* The application on the left, what people have said about it on
                  the right. A reviewer forms an opinion while reading the
                  details, and sending them to another tab to write it down is
                  how the reasoning ends up not written down at all. */}
              <Box
                sx={{
                  display: 'grid',
                  // Side by side from md, not lg. At lg the panel only appeared
                  // above 1200px of viewport, and below that it stacked BELOW an
                  // applicant form long enough to run off the screen — so on a
                  // laptop, or a window shared next to something else, the
                  // comments were there and nobody could find them.
                  // Comments get room to read a compliance note: half the page,
                  // 8 of 12 columns on a wide screen. A 360px rail wrapped every
                  // note into a long thin column.
                  gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr)', xl: 'minmax(0, 4fr) minmax(0, 8fr)' },
                  gap: 3,
                  alignItems: 'start',
                }}
              >
                <Box sx={{ minWidth: 0, order: { xs: 2, md: 1 } }}>
                  <EditContext.Provider
                    value={{
                      editing,
                      pending: pendingEdits,
                      stage: stageEdit,
                      completeOnly: !decidable,
                    }}
                  >
                    <ReviewContext.Provider
                      value={{ reviews: reviewsById, canDecide, decide: decideDocument }}
                    >
                      <SnapshotView
                        snapshot={snapshot}
                        applicationId={detail.id}
                        showPeople={false}
                        showDocuments={false}
                      />
                    </ReviewContext.Provider>
                  </EditContext.Provider>
                </Box>

                {/* First when stacked: a reviewer scrolling a long form to
                    the bottom to find the comments is a reviewer who does not
                    find them. */}
                <Box sx={{ order: { xs: 1, md: 2 } }}>
                  <CommentsPanel
                    subjectType="APPLICATION"
                    subjectId={detail.id}
                    customerId={detail.customerId}
                    canWrite={canDecide}
                    dense
                    // The whole client's history, not just this application's
                    // half of it. A note written here is still recorded
                    // against the application.
                    wholeClientHistory
                  />
                </Box>
              </Box>

              {pendingEdits.size > 0 ? (
                <Paper
                  elevation={4}
                  sx={{ position: 'sticky', bottom: 16, p: 2, mt: 2, border: '1px solid', borderColor: 'warning.main' }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    {String(pendingEdits.size)} unsaved change(s)
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    label="Reason for these changes (required)"
                    value={editReason}
                    onChange={(event) => setEditReason(event.target.value)}
                    sx={{ mb: 1 }}
                  />
                  {editError !== '' ? (
                    <Alert severity="error" sx={{ mb: 1 }}>
                      {editError}
                    </Alert>
                  ) : null}
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      disabled={editReason.trim().length < 4 || editMutation.isPending}
                      onClick={() => editMutation.mutate()}
                    >
                      {editMutation.isPending ? 'Saving…' : 'Save changes'}
                    </Button>
                    <Button onClick={resetEditing}>Discard</Button>
                  </Stack>
                </Paper>
              ) : null}
            </>
          ) : null}

          {tab === 1 ? (
            isBusiness ? (
              <>
                <EditContext.Provider
                  value={{ editing, pending: pendingEdits, stage: stageEdit }}
                >
                  <PeopleSection
                    snapshot={snapshot}
                    parties={partiesData?.parties ?? []}
                  />
                </EditContext.Provider>
                <Divider sx={{ my: 3 }} />
                {/* The company's own screening, beside its people's checks (DEV-89). */}
                <Box sx={{ mb: 3 }}>
                  <CompanyScreeningPanel customerId={detail.customerId} canFile={canDecide} />
                </Box>
                <PartyVerificationPanel customerId={detail.customerId} allowManage={editing} />
              </>
            ) : (
              /* The same panel a business gets. A personal applicant is now a
                 party in their own right — the SUBJECT of their own file — so
                 there is one identity interface rather than two that drift. */
              <PartyVerificationPanel customerId={detail.customerId} allowManage={editing} />
            )
          ) : null}

          {tab === 2 ? (
            <DocumentReviewSection
              applicationId={detail.id}
              customerId={detail.customerId}
              isBusiness={isBusiness}
              snapshot={snapshot}
              reviews={reviewsById}
              canDecide={canDecide}
              onDecide={decideDocument}
              onRecordDates={recordDocumentDates}
              onRemove={removeDocument}
              onUploaded={() => void refetchReview()}
              onRename={renameDocument}
            />
          ) : null}

          {tab === 3 ? (
            (editHistory ?? []).length === 0 && (decisionHistory ?? []).length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No decisions or changes have been recorded on this application yet.
              </Typography>
            ) : (
              <>
                {(decisionHistory ?? []).map((entry) => (
                  <Box
                    key={entry.id}
                    sx={{ mb: 1.5, pl: 1.5, borderLeft: '2px solid', borderColor: 'primary.main' }}
                  >
                    <Typography variant="body2">
                      <b>{entry.summary}</b>
                    </Typography>
                    {entry.reason !== null && entry.reason !== '' ? (
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                        {entry.reason}
                      </Typography>
                    ) : null}
                    <Typography variant="caption" color="text.secondary">
                      {new Date(entry.decidedAt).toLocaleString(APP_LOCALE)} · {entry.decidedBy}
                    </Typography>
                  </Box>
                ))}
                {(editHistory ?? []).map((edit) => (
                <Box
                  key={edit.id}
                  sx={{ mb: 1.5, pl: 1.5, borderLeft: '2px solid', borderColor: 'divider' }}
                >
                  <Typography variant="body2">
                    <b>{edit.fieldPath}</b>: {String(edit.oldValue ?? '—')} →{' '}
                    {String(edit.newValue ?? '—')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(edit.createdAt).toLocaleString(APP_LOCALE)} · {edit.adminId} · {edit.reason}
                  </Typography>
                </Box>
                ))}
              </>
            )
          ) : null}
        </Box>
      </Paper>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>{decision !== null ? DECISION_LABEL[decision] : ''} application</DialogTitle>
        <DialogContent>
          {/* Screening warns, it does not block (DEV-114): shown above the
              reason so the officer approves knowing it. */}
          {decision === 'APPROVE' ? (
            <Box sx={{ mt: 1, maxWidth: 560 }}>
              <ApprovalWarnings readiness={readiness} />
            </Box>
          ) : null}
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={4}
            label="Reason (required)"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            sx={{ mt: 1, minWidth: 420 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={reason.trim().length < 4 || decisionMutation.isPending}
            onClick={() => {
              if (decision !== null) {
                decisionMutation.mutate({ decision, reason: reason.trim() });
              }
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Navigating away after a terminal decision keeps the reviewer in the
          queue rather than staring at a page they can no longer act on. */}
      {decisionMutation.isSuccess && !decidable ? (
        <Box sx={{ mt: 2 }}>
          <Button onClick={() => void navigate('/onboarding-applications')}>
            Back to the queue
          </Button>
        </Box>
      ) : null}
    </>
  );
}
